import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import AuthService from '../../Services/auth.service';
import ChatApiService from '../../Services/chat.service';
import ClarificationDocumentService from '../../Services/clarificationDocuments.service';
import Constants from '../../assests/Constants/constants';
import ContactsList from '../Chat/SubComponents/ContactsList';
import ChatMessagesList from '../Chat/SubComponents/ChatMessagesList/ChatMessagesList';
import '../Chat/Chat.css';

const PAGE_SIZE = 20;
const CLIENT_USER_TYPE = Number(Constants.UserType.Client);

const isCurrentClientChatUser = (user, clientCaseId) => {
    if (!user?.userId || !clientCaseId) return false;
    const matchesCase = String(user.userId) === String(clientCaseId);
    if (!matchesCase) return false;
    const userType = Number(user.userType);
    return Number.isNaN(userType) || userType === CLIENT_USER_TYPE;
};

const toMessageType = (messageType) => {
    if (messageType === 1) return 'photo';
    if (messageType === 2) return 'file';
    if (messageType === 3) return 'audio';
    return 'text';
};

const formatMessageDate = (date) => {
    if (!date) return '';
    const dt = new Date(date);
    if (Number.isNaN(dt.getTime())) return '';
    const dd = `${dt.getDate()}`.padStart(2, '0');
    const mm = `${dt.getMonth() + 1}`.padStart(2, '0');
    const hh = `${dt.getHours()}`.padStart(2, '0');
    const min = `${dt.getMinutes()}`.padStart(2, '0');
    return `${dd}/${mm} ${hh}:${min}`;
};

export default function ClarificationConversationPickerModal({
    open,
    onClose,
    caseId,
    documentId,
    documentType,
    token,
    onAttached = () => { },
}) {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const scrollContainerRef = useRef(null);
    const messagesPaneRef = useRef(null);
    const loadingOlderRef = useRef(false);

    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [attaching, setAttaching] = useState(false);
    const [messagesListHeight, setMessagesListHeight] = useState(0);
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [messageSearchQuery, setMessageSearchQuery] = useState('');
    const [debouncedMessageSearch, setDebouncedMessageSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [currentUserId, setCurrentUserId] = useState('');
    const [currentUserType, setCurrentUserType] = useState('');
    const [currentUserName, setCurrentUserName] = useState('');
    const [highestPageLoaded, setHighestPageLoaded] = useState(1);
    const [lowestPageLoaded, setLowestPageLoaded] = useState(1);
    const [fetchedPages, setFetchedPages] = useState(() => new Set());
    const [hasMoreOlder, setHasMoreOlder] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedMessageSearch(messageSearchQuery.trim().toLowerCase());
        }, 250);
        return () => clearTimeout(timer);
    }, [messageSearchQuery]);

    useEffect(() => {
        if (!open) {
            setMessagesListHeight(0);
            return undefined;
        }

        const pane = messagesPaneRef.current;
        if (!pane) return undefined;

        const updateHeight = () => {
            const nextHeight = Math.floor(pane.getBoundingClientRect().height);
            setMessagesListHeight((prev) => (prev === nextHeight ? prev : nextHeight));
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(pane);
        window.addEventListener('resize', updateHeight);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, [open, selectedUser, loadingMessages, messageSearchQuery]);

    const resetMessagePaging = useCallback(() => {
        setMessages([]);
        setHighestPageLoaded(1);
        setLowestPageLoaded(1);
        setFetchedPages(new Set());
        setHasMoreOlder(true);
        loadingOlderRef.current = false;
    }, []);

    const mapFetchedMessage = useCallback((msg, viewerId, viewerName, chatUser) => {
        const isSentByCurrentUser = msg.senderId === viewerId;
        const senderName = isSentByCurrentUser
            ? viewerName
            : (chatUser?.userName || chatUser?.name || '');
        const receiverName = isSentByCurrentUser
            ? (chatUser?.userName || chatUser?.name || '')
            : viewerName;
        return {
            id: msg.id,
            position: isSentByCurrentUser ? 'right' : 'left',
            type: toMessageType(msg.messageType),
            text: msg.messageType === 0 ? (msg.messageContent || '') : '',
            date: msg.messageDate ? new Date(msg.messageDate) : null,
            dateString: formatMessageDate(msg.messageDate),
            receiverReadAt: msg.receiverReadAt ? new Date(msg.receiverReadAt) : null,
            title: senderName,
            senderId: msg.senderId,
            senderName,
            receiverId: msg.receiverId,
            receiverName,
            messageType: msg.messageType ?? 0,
            data: {
                uris: msg.messageType === 1 ? (msg.fileUrls || []) : [],
                fileURLs: msg.messageType === 2 ? (msg.fileUrls || []) : [],
                audioURLs: msg.messageType === 3 ? (msg.fileUrls || []) : [],
            },
            fileUrls: msg.fileUrls || [],
            replyTo: msg.replyToMessageId
                ? {
                    id: msg.replyToMessageId,
                    text: msg.replyPreview || t('MediaMessage'),
                }
                : null,
            replyToChatMessageId: msg.replyToMessageId || null,
            replyPreview: msg.replyPreview || null,
        };
    }, [t]);

    const fetchMessagesPage = useCallback(async (user, page, { prepend = false, replace = false } = {}) => {
        if (!user?.userId || !user?.userType || !currentUserId || !currentUserType) return [];
        const data = await ChatApiService.fetchMessages(
            currentUserId,
            currentUserType,
            user.userId,
            user.userType,
            page,
            PAGE_SIZE,
            false
        );
        const fetchedMessages = (data?.messages || [])
            .map((msg) => mapFetchedMessage(msg, currentUserId, currentUserName, user))
            .reverse();

        setFetchedPages((prev) => new Set(prev).add(page));
        if (fetchedMessages.length < PAGE_SIZE) {
            setHasMoreOlder(false);
        }

        if (replace) {
            setMessages(fetchedMessages);
            setHighestPageLoaded(page);
            setLowestPageLoaded(page);
        } else if (prepend) {
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => m.id));
                const unique = fetchedMessages.filter((m) => !existingIds.has(m.id));
                return [...unique, ...prev];
            });
            setHighestPageLoaded((prev) => Math.max(prev, page));
        } else {
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => m.id));
                const unique = fetchedMessages.filter((m) => !existingIds.has(m.id));
                return [...prev, ...unique];
            });
            setLowestPageLoaded((prev) => Math.min(prev, page));
        }

        return fetchedMessages;
    }, [currentUserId, currentUserType, currentUserName, mapFetchedMessage]);

    useEffect(() => {
        if (!open) return;
        let isMounted = true;
        (async () => {
            setLoadingUsers(true);
            setSelectedUser(null);
            resetMessagePaging();
            setMessageSearchQuery('');
            setUserSearchQuery('');
            try {
                const [userId, userType, userName] = await Promise.all([
                    AuthService.getCurrentUserTypeId(),
                    AuthService.getCurrentUserType(),
                    AuthService.getCurrentUserTypeName(),
                ]);
                if (!isMounted) return;
                setCurrentUserId(userId || '');
                setCurrentUserType(userType || '');
                setCurrentUserName(userName || '');
                const users = await ChatApiService.getAllowedUsers(userId, userType);
                if (!isMounted) return;
                const clientUsers = (users || []).filter((user) => isCurrentClientChatUser(user, caseId));
                setAllowedUsers(clientUsers);
                if (clientUsers.length === 1) {
                    setSelectedUser(clientUsers[0]);
                }
            } catch (error) {
                console.error('Error loading chat users for clarification attach:', error);
                toast.error(t('FailedToLoadChatUsers') || 'Failed to load chat users');
            } finally {
                if (isMounted) setLoadingUsers(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [open, caseId, t, resetMessagePaging]);

    useEffect(() => {
        if (!open || !selectedUser || !currentUserId || !currentUserType) return;
        let isMounted = true;
        (async () => {
            setLoadingMessages(true);
            resetMessagePaging();
            try {
                await fetchMessagesPage(selectedUser, 1, { replace: true });
            } catch (error) {
                console.error('Error loading chat messages for clarification attach:', error);
                toast.error(t('FailedToLoadMessages') || 'Failed to load messages');
            } finally {
                if (isMounted) setLoadingMessages(false);
            }
        })();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedUser?.userId, selectedUser?.userType, currentUserId, currentUserType]);

    const filteredAllowedUsers = useMemo(() => {
        const q = userSearchQuery.trim().toLowerCase();
        const scopedUsers = allowedUsers.filter((user) => isCurrentClientChatUser(user, caseId));
        if (!q) return scopedUsers;
        return scopedUsers.filter((user) =>
            user?.userName?.toLowerCase()?.includes(q) ||
            user?.userId?.toLowerCase()?.includes(q)
        );
    }, [allowedUsers, userSearchQuery, caseId]);

    const displayedMessages = useMemo(() => {
        if (!debouncedMessageSearch) return messages;
        return messages.filter((msg) => {
            const haystack = [
                msg.text,
                msg.senderName,
                msg.receiverName,
                msg.title,
                msg.replyPreview,
                msg.replyTo?.text,
                ...(msg.fileUrls || []),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(debouncedMessageSearch);
        });
    }, [messages, debouncedMessageSearch]);

    const loadOlderMessages = useCallback(async () => {
        if (!selectedUser || !hasMoreOlder || loadingOlderRef.current) return;
        const nextPage = highestPageLoaded + 1;
        if (fetchedPages.has(nextPage)) return;

        const container = scrollContainerRef.current;
        if (!container) return;

        loadingOlderRef.current = true;
        setLoadingOlder(true);
        const prevScrollHeight = container.scrollHeight;
        const prevScrollTop = container.scrollTop;
        try {
            await fetchMessagesPage(selectedUser, nextPage, { prepend: true });
            requestAnimationFrame(() => {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
            });
        } catch (error) {
            console.error('Error loading older clarification chat messages:', error);
        } finally {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
        }
    }, [selectedUser, hasMoreOlder, highestPageLoaded, fetchedPages, fetchMessagesPage]);

    const handleScroll = useCallback(({ scrollTop }) => {
        if (scrollTop <= 40) {
            loadOlderMessages();
        }
    }, [loadOlderMessages]);

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setMessageSearchQuery('');
    };

    const handleAttachMessages = async (selectedMessages) => {
        if (!selectedMessages?.length) return;
        if (!token || !caseId || !documentId || !documentType) {
            toast.error(t('MissingClarificationAttachContext') || 'Missing clarification attach context');
            return;
        }

        setAttaching(true);
        try {
            const payload = {
                caseId,
                documentId,
                documentType,
                messages: selectedMessages.map((msg) => ({
                    chatMessageId: msg.id || null,
                    senderId: msg.senderId,
                    senderName: msg.senderName || msg.title || '',
                    receiverId: msg.receiverId,
                    receiverName: msg.receiverName || '',
                    messageType: msg.messageType ?? 0,
                    text: msg.text || '',
                    fileUrls: msg.fileUrls || msg.data?.uris || msg.data?.fileURLs || msg.data?.audioURLs || [],
                    replyToChatMessageId: msg.replyToChatMessageId || msg.replyTo?.id || null,
                    replyPreview: msg.replyPreview || msg.replyTo?.text || '',
                    messageDate: msg.date ? new Date(msg.date).toISOString() : new Date().toISOString(),
                })),
            };

            const response = await ClarificationDocumentService.AttachClarificationMessages(payload, token);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Attach failed');
            }

            const result = await response.json();
            toast.success(
                t('ClarificationMessagesAttached', { count: result?.attachedCount ?? selectedMessages.length }) ||
                'Messages attached'
            );
            onAttached();
            onClose();
        } catch (error) {
            console.error('Error attaching clarification messages:', error);
            toast.error(error?.message || t('AttachClarificationMessagesFailed') || 'Failed to attach messages');
        } finally {
            setAttaching(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={attaching ? undefined : onClose}
            fullWidth
            maxWidth="xl"
            sx={{ zIndex: 1500 }}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    height: '85vh',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    direction,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderBottom: '1px solid #E5E7EB',
                    background: '#F8FAFF',
                    color: '#304FFF',
                    fontWeight: 700,
                    flexShrink: 0,
                }}
                dir={direction}
            >
                <span>{t('AttachConversation')}</span>
                <IconButton onClick={onClose} disabled={attaching}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    overflow: 'hidden',
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                dir={direction}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '320px 1fr',
                        flex: 1,
                        minHeight: 0,
                        height: '100%',
                        width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ borderInlineEnd: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
                            <TextField
                                fullWidth
                                placeholder={t('SearchUsers')}
                                value={userSearchQuery}
                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                    sx: {
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: 'white',
                                        border: '1px solid transparent',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        {loadingUsers ? (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                <ContactsList
                                    filteredAllowedUsers={filteredAllowedUsers}
                                    onUserSelect={handleUserSelect}
                                    isLoading={false}
                                    t={t}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden', background: '#F9FAFB' }}>
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB', background: '#fff', display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#111827' }}>
                                    {selectedUser?.userName || selectedUser?.name || t('SelectChatUser')}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                                    {t('SelectMultipleMessages')}
                                </Typography>
                            </Box>
                            {selectedUser && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('SearchMessages') || t('Search') || 'Search messages'}
                                    value={messageSearchQuery}
                                    onChange={(e) => setMessageSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            background: '#fff',
                                            borderRadius: '10px',
                                        },
                                    }}
                                />
                            )}
                            {loadingOlder && (
                                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                                    {t('LoadingOlderMessages') || t('Loading') || 'Loading older messages...'}
                                </Typography>
                            )}
                        </Box>
                        {loadingMessages ? (
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress />
                            </Box>
                        ) : selectedUser ? (
                            <Box
                                ref={messagesPaneRef}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    height: 0,
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                {displayedMessages.length === 0 && debouncedMessageSearch ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                                        <Typography sx={{ color: '#6B7280' }}>
                                            {t('NoMessagesFound') || t('NoMessages') || 'No messages found'}
                                        </Typography>
                                    </Box>
                                ) : messagesListHeight > 0 ? (
                                    <ChatMessagesList
                                        messages={displayedMessages}
                                        containerHeight={messagesListHeight}
                                        highestPageLoaded={highestPageLoaded}
                                        lowestPageLoaded={lowestPageLoaded}
                                        getAllForReceiver={false}
                                        refreshMessages={() => fetchMessagesPage(selectedUser, 1, { replace: true })}
                                        scrollContainerRef={scrollContainerRef}
                                        onScroll={handleScroll}
                                        onAttachMessages={handleAttachMessages}
                                        attachActionLabel={attaching ? (t('Attaching') || 'Attaching...') : t('AttachConversation')}
                                        onForwardMessages={null}
                                        onReplyToMessage={null}
                                        allowDeleteSelected={false}
                                        defaultMultiSelectMode
                                        hideExitMultiSelect
                                    />
                                ) : null}
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                                <Typography sx={{ color: '#6B7280' }}>
                                    {t('SelectChatUser')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
